import UserFinanceDataServices, {
  UserFinanceDataResponse,
} from "@/services/userFinanceDataServices";
import { create } from "zustand";

interface FetchOptions {
  silent?: boolean;
}

interface FinanceDataState {
  financeData: UserFinanceDataResponse | null;
  isLoading: boolean;
  fetchFinanceData: (options?: FetchOptions) => Promise<void>;
  setFinanceData: (data: UserFinanceDataResponse | null) => void;
  updateIncome: (income: number) => Promise<boolean>;
  updateBudget: (totalBudget: number) => Promise<boolean>;
  updateCategoryBudgets: (categories: Array<{ category: string; budgetAmount: number }>) => Promise<boolean>;
}

const useFinanceDataStore = create<FinanceDataState>((set, get) => ({
  financeData: null,
  isLoading: false,

  fetchFinanceData: async (options?: FetchOptions) => {
    const silent = options?.silent ?? false;
    if (!silent) {
      set({ isLoading: true });
    }
    try {
      const res = await UserFinanceDataServices.get();
      if (res.success) {
        set({ financeData: res.data });
      }
    } finally {
      if (!silent) {
        set({ isLoading: false });
      }
    }
  },

  setFinanceData: (data) => {
    set({ financeData: data });
  },

  updateIncome: async (income: number) => {
    try {
      const res = await UserFinanceDataServices.config({ income });
      if (res.success && res.data?.analysis) {
        const current = get().financeData;
        if (current) {
          set({
            financeData: {
              ...current,
              analysis: res.data.analysis,
            },
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to update income:", error);
      return false;
    }
  },

  updateBudget: async (totalBudget: number) => {
    try {
      const res = await UserFinanceDataServices.config({ totalBudget });
      console.log("[updateBudget] API response:", JSON.stringify(res));
      if (res.success && res.data?.analysis) {
        const current = get().financeData;
        if (current) {
          set({
            financeData: {
              ...current,
              analysis: res.data.analysis,
            },
          });
        }
        return true;
      }
      console.error("[updateBudget] Failed - success:", res.success, "hasAnalysis:", !!res.data?.analysis, "message:", res.message);
      return false;
    } catch (error) {
      console.error("[updateBudget] Exception:", error);
      return false;
    }
  },

  updateCategoryBudgets: async (categories: Array<{ category: string; budgetAmount: number }>) => {
    try {
      const res = await UserFinanceDataServices.config({ categories });
      console.log("[updateCategoryBudgets] API response:", JSON.stringify(res));
      if (res.success && res.data?.analysis) {
        const current = get().financeData;
        if (current) {
          set({
            financeData: {
              ...current,
              analysis: res.data.analysis,
            },
          });
        }
        return true;
      }
      console.error("[updateCategoryBudgets] Failed - success:", res.success, "hasAnalysis:", !!res.data?.analysis, "message:", res.message);
      return false;
    } catch (error) {
      console.error("[updateCategoryBudgets] Exception:", error);
      return false;
    }
  },
}));

export default useFinanceDataStore;
